// https://developers.google.com/analytics/devguides/collection/ga4/reference/events
import { logDebug, logWarn, logError, logEvent } from '../../tracing.js';

import {
    map,
    isArray,
    find,
    some,
    includes,
    forEach,
    isNil,
    isEmpty,
} from 'lodash';

import { validateGoogleData } from '../data/dataPageService.js';

const _prefix = 'GA4';
const _validateGtag = () => {
    if (!window.gtag) {
        throw new Error('window.gtag is null or undefined');
    }
};

const _setCategories = ({ itemObj, categories }) => {
    forEach(categories, (category, index) => {
        itemObj[`item_category${index === 0 ? '' : index}`] = category;
    });
    return itemObj;
};

const _mapItems = (items) =>
    map(items, (item) => {
        const itemObj = {
            item_id: item.googleId,
            item_name: item.slug,
        };
        if (!isNil(item.quantity)) {
            itemObj.quantity = item.quantity;
        }
        if (!isNil(item.currency)) {
            itemObj.currency = item.currency;
        }
        if (!isNil(item.price)) {
            itemObj.price = item.price;
        }
        if (!isNil(item.disciplines)) {
            itemObj.disciplines = item.disciplines;
        }
        if (!isNil(item.brandName)) {
            itemObj.item_brand = item.brandName;
        }
        if (
            !isNil(item.categories) &&
            item.categories &&
            item.categories.length > 0
        ) {
            return _setCategories({ itemObj, categories: item.categories });
        }
        return itemObj;
    });

const beginCheckout = ({ currency, totaleuro, items }) => {
    _validateGtag();
    const analytics4MeasurementId = validateGoogleData(
        'analytics4MeasurementId'
    );
    const analytics4MeasurementGlobalId = validateGoogleData(
        'analytics4MeasurementGlobalId'
    );

    const obj = {
        send_to: [analytics4MeasurementId, analytics4MeasurementGlobalId],
        // transaction_id: cartId, // CREDO NON sia passabile, non è documentato e non mi pare registrare l'evento se lo aggiungo.
        currency,
        value: totaleuro,
        items: _mapItems(items),
    };
    logEvent(`${_prefix} begin_checkout`, obj);
    gtag('event', 'begin_checkout', obj);
};

const purchase = ({ transaction_id, currency, value, shipping, items }) => {
    _validateGtag();
    const analytics4MeasurementId = validateGoogleData(
        'analytics4MeasurementId'
    );
    const analytics4MeasurementGlobalId = validateGoogleData(
        'analytics4MeasurementGlobalId'
    );

    let obj = {
        transaction_id,
        send_to: [analytics4MeasurementId, analytics4MeasurementGlobalId],
        currency,
        value,
        shipping,
        items: items.map((li) => ({
            item_id: li.googleId,
            item_name: li.slug,
            item_category:
                li.categories &&
                isArray(li.categories) &&
                li.categories.length > 0
                    ? li.categories[0]
                    : '',
            item_brand:
                isNil(li.brandName) || isEmpty(li.brandName)
                    ? ''
                    : li.brandName,
            price: li.price,
            quantity: li.quantity,
        })),
    };
    logEvent(`${_prefix} purchase`, obj);
    gtag('event', 'purchase', obj);
};

const newsletterRegistration = ({ url, email }) => {
    _validateGtag();
    const analytics4MeasurementId = validateGoogleData(
        'analytics4MeasurementId'
    );
    const analytics4MeasurementGlobalId = validateGoogleData(
        'analytics4MeasurementGlobalId'
    );

    let obj = {
        send_to: [analytics4MeasurementId, analytics4MeasurementGlobalId],
        url: url === undefined ? window.location.href : url,
        email,
    };
    logEvent(`${_prefix} CompleteRegistration`, obj);
    gtag('event', 'CompleteRegistration', obj);
};

const viewItem = ({ currency, value, items }) => {
    _validateGtag();
    const analytics4MeasurementId = validateGoogleData(
        'analytics4MeasurementId'
    );
    const analytics4MeasurementGlobalId = validateGoogleData(
        'analytics4MeasurementGlobalId'
    );

    const obj = {
        send_to: [analytics4MeasurementId, analytics4MeasurementGlobalId],

        currency,
        value,
        items: _mapItems(items),
    };

    logEvent(`${_prefix} view_item`, obj);
    gtag('event', 'view_item', obj);
};

const searchItemsResults = ({ searchterm }) => {
    // NOTA: abbiamo disabilitato l'enhancement measure su GA4 (stream web)
    // al fine di evitare duplicazione di questi eventi.
    // Disabilitandolo, nei report ne risulta uno,
    // anche se comunque ne vengono sparati due (uno dal nostro codice e uno by default da Google)
    _validateGtag();
    const obj = {
        search_term: searchterm,
    };

    logEvent(`${_prefix} search`, obj);
    gtag('event', 'search', obj);
};

const viewItemList = ({ items }) => {
    _validateGtag();
    const analytics4MeasurementId = validateGoogleData(
        'analytics4MeasurementId'
    );
    const analytics4MeasurementGlobalId = validateGoogleData(
        'analytics4MeasurementGlobalId'
    );

    const obj = {
        send_to: [analytics4MeasurementId, analytics4MeasurementGlobalId],
        items: _mapItems(items),
    };

    logEvent(`${_prefix} view_item_list`, obj);
    gtag('event', 'view_item_list', obj);
};

const viewCart = ({ cartId, currency, totaleuro, items }) => {
    _validateGtag();
    const obj = {
        // transaction_id: cartId,
        currency,
        value: parseFloat(totaleuro),
        items: _mapItems(items),
    };

    logEvent(`${_prefix} view_cart`, obj);
    gtag('event', 'view_cart', obj);
};

const addShippingInfo = ({ cartId, currency, value, items, shipping_tier }) => {
    _validateGtag();
    const obj = {
        // transaction_id: cartId, // NON SI PUO' METTERE o NON REGISTRA L'EVENTO
        currency,
        value: parseFloat(value),
        items: _mapItems(items),
        shipping_tier: shipping_tier,
    };
    logEvent(`${_prefix} add_shipping_info`, obj);
    gtag('event', 'add_shipping_info', obj);
};

const addToCart = ({
    id,
    quantity,
    currency,
    price,
    items,
    disciplines,
    categories,
    brandName,
}) => {
    _validateGtag();

    if (!some(items, (item) => item.googleId === id)) {
        logWarn(
            `Mismatch between serverside ids and current clientside id ${id} on add_to_cart. Serverside ids: ${map(
                items,
                (i) => i.googleId
            )}`
        );
    }
    const analytics4MeasurementId = validateGoogleData(
        'analytics4MeasurementId'
    );
    const analytics4MeasurementGlobalId = validateGoogleData(
        'analytics4MeasurementGlobalId'
    );
    const obj = {
        send_to: [analytics4MeasurementId, analytics4MeasurementGlobalId],
        currency,
        value: find(items, (item) => item.googleId === id).price,
        items: _mapItems([
            {
                googleId: id,
                slug: find(items, (item) => item.googleId === id).slug,
                quantity,
                currency,
                price: find(items, (item) => item.googleId === id).price,
                disciplines,
                brandName,
                categories,
            },
        ]),
    };

    logEvent(`${_prefix} add_to_cart`, obj);
    gtag('event', 'add_to_cart', obj);
};

const addPaymentInfo = ({ currency, value, items, payment }) => {
    _validateGtag();
    const obj = {
        currency,
        value,
        payment_type: payment,
        items: _mapItems(items),
    };
    logEvent(`${_prefix} add_payment_info`, obj);
    gtag('event', 'add_payment_info', obj);
};

const closePopUp = () => {
    _validateGtag();
    const analytics4MeasurementId = validateGoogleData(
        'analytics4MeasurementId'
    );
    const analytics4MeasurementGlobalId = validateGoogleData(
        'analytics4MeasurementGlobalId'
    );

    const obj = {
        send_to: [analytics4MeasurementId, analytics4MeasurementGlobalId],
        content_type: 'close pop up',
    };

    logEvent(`${_prefix} select_content`, obj);
    gtag('event', 'select_content', obj);
};

const popUpButtonClick = () => {
    _validateGtag();
    const analytics4MeasurementId = validateGoogleData(
        'analytics4MeasurementId'
    );
    const analytics4MeasurementGlobalId = validateGoogleData(
        'analytics4MeasurementGlobalId'
    );

    const obj = {
        send_to: [analytics4MeasurementId, analytics4MeasurementGlobalId],
        content_type: 'pop up button click',
    };

    logEvent(`${_prefix} select_content`, obj);
    gtag('event', 'select_content', obj);
};

const init = ({ debug }) => {
    // Analytics 4
    if (!window.dataLayer) {
        logDebug('window.dataLayer does not exist. Creating it as empty array');
    }
    window.dataLayer = window.dataLayer || [];
    logDebug('window.dataLayer set...');
    function gtag() {
        dataLayer.push(arguments);
    }
    window.gtag = gtag;
    _validateGtag();
    logDebug('window.gtag set...');
    gtag('js', new Date());
};
const setConfigGoogleAnalytics4 = ({ debug }) => {
    _validateGtag();
    // Questo config in automatico, per default, setta send_page_view a true per GA4 (con dei parametri di default).
    const analytics4MeasurementId = validateGoogleData(
        'analytics4MeasurementId'
    );

    const configAlreadySetAnalytics4MeasurementId = some(
        dataLayer,
        (d) => includes(d, 'config') && includes(d, analytics4MeasurementId)
    );
    if (!configAlreadySetAnalytics4MeasurementId) {
        if (debug === true) {
            gtag('config', analytics4MeasurementId, {
                debug_mode: debug,
            });
        } else {
            gtag('config', analytics4MeasurementId);
        }
        logEvent(
            `GA4 pageview via \'config\' for measurement id ${analytics4MeasurementId}`
        );
    }

    const analytics4MeasurementGlobalId = validateGoogleData(
        'analytics4MeasurementGlobalId'
    );

    const configAlreadySetAnalytics4MeasurementGlobalId = some(
        dataLayer,
        (d) =>
            includes(d, 'config') && includes(d, analytics4MeasurementGlobalId)
    );
    if (!configAlreadySetAnalytics4MeasurementGlobalId) {
        if (debug === true) {
            gtag('config', configAlreadySetAnalytics4MeasurementGlobalId, {
                debug_mode: debug,
            });
        } else {
            gtag('config', configAlreadySetAnalytics4MeasurementGlobalId);
        }
        logEvent(
            `GA4 pageview via \'config\' for Global measurement id ${analytics4MeasurementGlobalId}`
        );
    }
};

const recordGA4BannerClick = ({ linkUrl, title, index }) => {
    _validateGtag();
    const obj = {
        content_type: 'banner',
        item_id: linkUrl,
        title,
        index,
    };
    logEvent(
        `GA4 custom event BANNER CLICK: ${linkUrl} ${title} ${index}`,
        obj
    );
    gtag('event', 'select_content', obj);
};

const recordGA4PromoImagesClick = ({
    linkUrl,
    promoMessage,
    index,
    imageSrc,
}) => {
    _validateGtag();
    const obj = {
        content_type: 'promoImages',
        item_id: linkUrl,
        promoMessage,
        index,
        imageSrc,
    };
    logEvent(
        `GA4 custom event PROMO IMAGES CLICK: ${linkUrl} ${promoMessage.message} ${index}`,
        obj
    );
    gtag('event', 'select_content', obj);
};

const recordGA4InfoboxClick = ({ linkUrl }) => {
    _validateGtag();
    const obj = {
        content_type: 'infobox',
        item_id: linkUrl,
    };
    logEvent(`GA4 custom event INFOBOX CLICK: ${linkUrl}`, obj);
    gtag('event', 'select_content', obj);
};

const recordGA4NewsHomepageClick = () => {
    _validateGtag();
    const obj = {
        content_type: 'news',
    };
    logEvent(`GA4 custom event NEWS HOMEPAGE`, obj);
    gtag('event', 'select_content', obj);
};

const recordGA4HelpClick = () => {
    _validateGtag();
    const obj = {
        content_type: 'help',
    };
    logEvent(`GA4 custom event HELP PAGE`, obj);
    gtag('event', 'select_content', obj);
};

const recordGA4CategoryClick = ({ linkUrl }) => {
    _validateGtag();
    const obj = {
        content_type: 'category',
        item_id: linkUrl,
    };
    logEvent(`GA4 custom event CATEGORY CLICK: ${linkUrl}`, obj);
    gtag('event', 'select_content', obj);
};

const recordGA4LogonClick = ({ linkUrl }) => {
    _validateGtag();
    const obj = {
        content_type: 'logon',
        item_id: linkUrl,
    };
    logEvent(`GA4 custom event LOGON CLICK: ${linkUrl}`, obj);
    gtag('event', 'select_content', obj);
};

const recordGA4RegisterClick = ({ linkUrl }) => {
    _validateGtag();
    const obj = {
        content_type: 'register',
        item_id: linkUrl,
    };
    logEvent(`GA4 custom event REGISTER CLICK: ${linkUrl}`, obj);
    gtag('event', 'select_content', obj);
};

export {
    beginCheckout,
    purchase,
    newsletterRegistration,
    viewItem,
    searchItemsResults,
    viewItemList,
    viewCart,
    addShippingInfo,
    addToCart,
    addPaymentInfo,
    closePopUp,
    popUpButtonClick,
    init,
    setConfigGoogleAnalytics4,
    recordGA4BannerClick,
    recordGA4PromoImagesClick,
    recordGA4InfoboxClick,
    recordGA4NewsHomepageClick,
    recordGA4HelpClick,
    recordGA4CategoryClick,
    recordGA4LogonClick,
    recordGA4RegisterClick,
};
